import React, { useEffect, useRef } from 'react';
import { motion, useSpring, useTransform, useInView } from 'framer-motion';
import { STATS } from '../../constants';

// Animated counter that handles "~30%", "6x", "150+", "0"
const StatValue: React.FC<{ raw: string }> = ({ raw }) => {
  const match = raw.match(/^([^0-9]*)(\d+)(.*)$/);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const num = match ? parseInt(match[2], 10) : 0;
  const spring = useSpring(0, { mass: 0.8, stiffness: 70, damping: 16 });
  const display = useTransform(
    spring,
    current => `${match?.[1] ?? ''}${Math.round(current)}${match?.[3] ?? ''}`
  );

  useEffect(() => {
    if (isInView) spring.set(num);
  }, [isInView, num, spring]);

  if (!match) return <span>{raw}</span>;
  return <motion.span ref={ref}>{display}</motion.span>;
};

const Stats: React.FC = () => {
  return (
    <section className='max-w-7xl mx-auto px-5 sm:px-6'>
      <div className='grid grid-cols-2 lg:grid-cols-4 border-t border-line'>
        {STATS.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.55, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
            className='group py-10 sm:py-14 px-2 sm:px-6 border-b border-line lg:border-b-0 text-center'
          >
            <div className='font-medium tracking-tight text-[clamp(2.2rem,4.5vw,3.75rem)] leading-none text-ink group-hover:text-accent transition-colors duration-400'>
              <StatValue raw={stat.value} />
            </div>
            <div className='label-mono text-gray1 mt-4'>{stat.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Stats;
